import classRoomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import "../../TeacherAdmin/classroom/Classroom.css";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import "../../TeacherAdmin/classroom/CreateClassroom.css";
import SchoolAdminStudentSelector from "./SchoolAdminStudentSelector";
import CustomInput from "../../../common/CustomInput";
import { useCreateClassroom } from "../../../../hooks/teacher/classroom/useCreateClassroom";

import { useAssignStudents } from "../../../../hooks/teacher/classroom/useAssignStudents";
import { useRef, useState } from "react";
import BackButton from "../Goback/GoBack";
import { Button } from "react-bootstrap";
import SuccessModal from "../../../common/SuccessModal";
import ScrollToError from "../../../../utils/ScrollToError";

export const schoolAdminClassroomValidationSchema = Yup.object().shape({
  teacher: Yup.object().nullable().required("Please select a teacher"),
  classroomName: Yup.string()
    .trim()
    .required("Classroom name is required")
    .min(3, "Classroom name must be at least 3 characters long")
    .max(100, "Classroom name must be at most 100 characters long")
    .matches(
      /^[A-Za-z0-9 _-]+$/,
      "Classroom name can only contain letters, numbers, spaces, underscores, and hyphens"
    ),
  students: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        student_code: Yup.string(),
      }),
    )
    .min(1, "Please select at least one student"),
});



const SchoolAdminCreateClassroom = () => {
  const { loading, error, createClassroom } = useCreateClassroom();
  const { loading: assignLoading, assignStudents } = useAssignStudents();
  const topRef = useRef(null);
  const classroomNameRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const refreshCountRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const validationSchema = schoolAdminClassroomValidationSchema;

  const [successMessage, setSuccessMessage] = useState({
    message: "",
    description: "",
    type: "success",
  });

  const initialValues = {
    teacher: null,
    classroomName: "",
    students: [],
  };

  /**
   * Handles classroom creation and student assignment
   */
  const handleCreateClassroom = async (values, { resetForm }) => {
    setSuccessMessage({
      message: "",
      description: "",
      type: "success",
    });

    // Step 1: Create the classroom using the selected teacher's ID
    const response = await createClassroom({
      teacher_id: values.teacher.id,
      name: values.classroomName,
    });

    if (!response) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
      return;
    }

    // Step 2: Assign students to the classroom
    const classroomId = response.data.id;
    const studentsToAssign = values.students.map((student) => ({
      id: student.id,
      is_assign: true,
    }));

    const assignResponse = await assignStudents({
      classroomId,
      studentsData: studentsToAssign,
    });

    if (!assignResponse) {
      setSuccessMessage({
        message: "Classroom created successfully!",
        description:
          "Classroom created successfully, but there was an error while assigning the students to the class. Please try again by updating the classroom.",
        type: "error",
      });
      setShowModal(true);
      topRef.current.scrollIntoView({ behavior: "smooth" });
      return;
    }

    // Everything succeeded
    setSuccessMessage({
      message: "Classroom created successfully!",
      description:
        "Classroom has been created successfully and students have been assigned to the classroom.",
      type: "success",
    });
    setShowModal(true);
    resetForm();
    refreshCountRef.current += 1;
    setRefreshKey(refreshCountRef.current);
    topRef.current.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      <div>
        <BackButton />
      </div>
      <div className="classroom-cnt">
        <div className="classroom-container" ref={topRef}>
          <img src={classRoomImg} className="img" alt="Classroom" />
          <h2 className="text">Create Classroom</h2>
        </div>

        <div>
          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}
        </div>

        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleCreateClassroom}
            enableReinitialize
          >
            {({ isSubmitting, errors, touched }) => (
              <Form>
                <ScrollToError
                  fieldRefs={{ classroomName: classroomNameRef }}
                />
                <div className="mt-0 input-container">
                  <CustomInput
                    type="text"
                    name="classroomName"
                    label="Classroom Name"
                    placeholder="Enter classroom name"
                    autoComplete="off"
                    layout="inline"
                    ref={classroomNameRef}
                  />
                </div>



                <div className="mt-4">
                  <SchoolAdminStudentSelector
                    name="students"
                    teacherFieldName="teacher"
                    refresh={refreshKey}
                  />
                  {/* Teacher validation error */}
                  {touched.teacher && errors.teacher && (
                    <div className="invalid-feedback d-block mt-2">
                      <i className="bi bi-exclamation-circle-fill me-1"></i>
                      {errors.teacher}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  className="custom-primary-button mt-4"
                  disabled={loading || assignLoading}
                >
                  {loading || assignLoading
                    ? "Creating Classroom..."
                    : "Save Classroom"}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <SuccessModal
        type={successMessage.type}
        showModal={showModal}
        handleClose={setShowModal}
        title={successMessage.message}
        message={successMessage.description}
        primaryButtonText="Add Another"
        primaryButtonLink="/school-admin/classroom/create"
        secondaryButtonText="Classrooms List"
        secondaryButtonLink="/school-admin/classroom"
        handlePrimaryClickCallback={() => {
          refreshCountRef.current += 1;
          setRefreshKey(refreshCountRef.current);
        }}
      />
    </div>
  );
};

export default SchoolAdminCreateClassroom;
